/**
 * AiStudio
 *
 * The single component the Add Content form mounts to get the whole AI experience:
 * the launcher modal, the background run, and the progress dock.
 *
 * It owns the `useAiStudio` orchestrator so the container's wiring stays a handful
 * of callbacks (`onMetadata`, `onSubtitles`, …) rather than five hooks and a
 * scheduler. Everything about *where results go* stays in the container, which is
 * the same split the individual AI hooks already use.
 *
 * ── Trigger behaviour ────────────────────────────────────────────────────────
 * The launcher opens by itself the first time a video is selected, because that is
 * the moment the operator has a decision to make and the point at which AI has
 * something to work with. It opens ONCE per video — dismiss it and it stays shut;
 * a small "Use AI" button remains so it can be reopened deliberately. Selecting a
 * different video is treated as a new decision and offers the launcher again.
 *
 * ── Safety ───────────────────────────────────────────────────────────────────
 * Rendering nothing when `enabled` is false is what makes this safe to ship to a
 * live app: with the flag off, the form behaves exactly as it did before, and the
 * existing per-section AI panels remain the working path.
 */

import React, { useEffect, useRef, useState } from "react";
import { HiSparkles } from "react-icons/hi2";
import style from "./aiStudio.module.css";
import AiStudioLauncher from "./AiStudioLauncher";
import AiStudioDock from "./AiStudioDock";
import useAiStudio from "../../hooks/useAiStudio";

const AiStudio = ({
  /** Master switch — false renders nothing at all. */
  enabled = true,
  /**
   * Whether selecting a video should pop the launcher by itself. True when adding
   * new content (the operator has a genuine decision to make); false when editing,
   * where the form is already populated and an unprompted modal would just be in
   * the way — there the "Use AI" button is the deliberate way in.
   */
  autoOpen = true,
  /** The selected video descriptor passed to every job. */
  video,
  /** Stable identity for `video`; a change means "new video selected". */
  videoKey,
  /** Display name shown in the launcher. */
  videoName,
  /** Language codes that already have subtitles / audio tracks (chip ✓ marks). */
  existingSubtitleLangs,
  existingDubLangs,
  /** Result handlers — the container decides what to do with each. */
  onMetadata,
  onSubtitles,
  onDub,
  onTrailer,
  onThumbnails,
  /** Called when the operator explicitly chooses to fill the form by hand. */
  onManual,
}) => {
  const [launcherOpen, setLauncherOpen] = useState(false);
  /** videoKey values we've already offered the launcher for. */
  const offeredRef = useRef(new Set());

  const studio = useAiStudio({
    video,
    onMetadata,
    onSubtitles,
    onDub,
    onTrailer,
    onThumbnails,
  });

  // Offer the launcher once per newly selected video.
  useEffect(() => {
    if (!enabled || !autoOpen || !videoKey) return;
    if (offeredRef.current.has(videoKey)) return;
    offeredRef.current.add(videoKey);
    // Don't interrupt a run that's already in flight for a previous selection.
    if (studio.isRunning) return;
    setLauncherOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, autoOpen, videoKey]);

  if (!enabled) return null;

  const handleStart = (config) => {
    setLauncherOpen(false);
    studio.run(config);
  };

  const handleManual = () => {
    setLauncherOpen(false);
    onManual?.();
  };

  const showReopen = !launcherOpen && studio.isIdle && Boolean(videoKey);

  return (
    <>
      <AiStudioLauncher
        open={launcherOpen}
        videoName={videoName}
        existingSubtitleLangs={existingSubtitleLangs}
        existingDubLangs={existingDubLangs}
        onStart={handleStart}
        onManual={handleManual}
        onClose={() => setLauncherOpen(false)}
      />

      {!studio.isIdle && (
        <AiStudioDock
          tasks={studio.tasks}
          counts={studio.counts}
          overallProgress={studio.overallProgress}
          elapsedMs={studio.elapsedMs}
          isRunning={studio.isRunning}
          isReview={studio.isReview}
          onCancel={studio.cancel}
          onRetryTask={studio.retryTask}
          onDone={studio.acknowledge}
        />
      )}

      {showReopen && (
        <button
          type="button"
          className={style.reopenBtn}
          onClick={() => setLauncherOpen(true)}
          title="Generate content details with AI"
        >
          <HiSparkles />
          Use AI
        </button>
      )}
    </>
  );
};

export default AiStudio;
